import { t } from "@lingui/macro";
import { defaultResumeData } from "@stylingcv/schema";
import { Checkbox, Input, Label, ToggleGroup, ToggleGroupItem, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useEffect, useMemo, useRef } from "react";

import { useResumeStore } from "@/client/stores/resume";

// Aspect Ratio Helpers
const stringToRatioMap = {
  square: 1,
  portrait: 0.75,
  horizontal: 1.33,
} as const;

const ratioToStringMap = {
  "1": "square",
  "0.75": "portrait",
  "1.33": "horizontal",
} as const;

type AspectRatio = keyof typeof stringToRatioMap;

// Border Radius Helpers
const stringToBorderRadiusMap = {
  square: 0,
  rounded: 6,
  circle: 9999,
};

const borderRadiusToStringMap = {
  "0": "square",
  "6": "rounded",
  "9999": "circle",
};

type BorderRadius = keyof typeof stringToBorderRadiusMap;

export const PictureOptions = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const picture = useResumeStore((state) => {
    try {
      return state.resume.data.basics.picture || defaultResumeData.basics.picture;
    } catch {
      return defaultResumeData.basics.picture;
    }
  });

  const template = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.template;
    } catch {
      return "leafish";
    }
  });

  const isStraightTemplate = template === "straight";
  const previousTemplate = useRef(template);

  useEffect(() => {
    if (isStraightTemplate) {
      setValue("basics.picture.effects.hidden", true);
    } else if (previousTemplate.current === "straight" && template !== "straight") {
      setValue("basics.picture.effects.hidden", false);
    }
    previousTemplate.current = template;
  }, [template, isStraightTemplate, setValue]);

  const aspectRatio = useMemo(() => {
    const ratio = picture.aspectRatio.toString() as keyof typeof ratioToStringMap;
    return ratioToStringMap[ratio];
  }, [picture.aspectRatio]);

  const onAspectRatioChange = (value: string) => {
    if (!value) return;
    setValue("basics.picture.aspectRatio", stringToRatioMap[value as AspectRatio]);
  };

  const borderRadius = useMemo(() => {
    const radius = picture.borderRadius.toString() as keyof typeof borderRadiusToStringMap;
    return borderRadiusToStringMap[radius];
  }, [picture.borderRadius]);

  const onBorderRadiusChange = (value: string) => {
    if (!value) return;
    setValue("basics.picture.borderRadius", stringToBorderRadiusMap[value as BorderRadius]);
  };

  const rowClass = "flex items-center justify-between gap-4";
  const labelClass = "text-[12px] font-medium text-[var(--fg-2,#404040)] shrink-0";
  const toggleItemClass =
    "flex items-center justify-center rounded border border-[var(--border,#e5e5e5)] p-1.5 h-auto text-[var(--fg-3,#737373)] transition-all hover:border-[var(--accent,#268599)] hover:text-[var(--accent,#268599)] data-[state=on]:border-[var(--accent,#268599)] data-[state=on]:bg-[var(--accent,#268599)]/8 data-[state=on]:text-[var(--accent,#268599)]";
  const checkboxClass =
    "size-3.5 rounded border-[var(--border,#e5e5e5)] data-[state=checked]:bg-[var(--accent,#268599)] data-[state=checked]:border-[var(--accent,#268599)]";

  return (
    <div className="flex flex-col gap-2.5 p-0.5">

      {/* Size */}
      <div className={rowClass}>
        <Label htmlFor="picture.size" className={labelClass}>{t`Size`}</Label>
        <Input
          type="number"
          id="picture.size"
          placeholder="128"
          value={picture.size}
          className="h-7 w-20 border-[var(--border,#e5e5e5)] bg-white text-center text-sm text-[var(--fg-1,#171717)] focus-visible:border-[var(--accent,#268599)] focus-visible:ring-1 focus-visible:ring-[var(--accent,#268599)] [&::-webkit-inner-spin-button]:m-0.5 [&::-webkit-inner-spin-button]:opacity-100"
          onChange={(event) => setValue("basics.picture.size", event.target.valueAsNumber)}
        />
      </div>

      {/* Aspect Ratio */}
      <div className={rowClass}>
        <Label className={labelClass}>{t`Ratio`}</Label>
        <div className="flex items-center gap-1.5">
          <ToggleGroup type="single" value={aspectRatio} className="flex gap-1" onValueChange={onAspectRatioChange}>
            <Tooltip content={t`Square`}>
              <ToggleGroupItem value="square" className={toggleItemClass}>
                <div className="size-3.5 border-[1.5px] border-current" />
              </ToggleGroupItem>
            </Tooltip>
            <Tooltip content={t`Landscape`}>
              <ToggleGroupItem value="horizontal" className={toggleItemClass}>
                <div className="h-2.5 w-3.5 border-[1.5px] border-current" />
              </ToggleGroupItem>
            </Tooltip>
            <Tooltip content={t`Portrait`}>
              <ToggleGroupItem value="portrait" className={toggleItemClass}>
                <div className="h-3.5 w-2.5 border-[1.5px] border-current" />
              </ToggleGroupItem>
            </Tooltip>
          </ToggleGroup>
          <span className="w-7 text-right text-[10px] font-mono text-[var(--fg-4,#a3a3a3)]">{picture.aspectRatio}</span>
        </div>
      </div>

      {/* Corner Style */}
      <div className={rowClass}>
        <Label className={labelClass}>{t`Corners`}</Label>
        <div className="flex items-center gap-1.5">
          <ToggleGroup type="single" value={borderRadius} className="flex gap-1" onValueChange={onBorderRadiusChange}>
            <Tooltip content={t`Square`}>
              <ToggleGroupItem value="square" className={toggleItemClass}>
                <div className="size-3.5 border-[1.5px] border-current" />
              </ToggleGroupItem>
            </Tooltip>
            <Tooltip content={t`Rounded`}>
              <ToggleGroupItem value="rounded" className={toggleItemClass}>
                <div className="size-3.5 rounded-[3px] border-[1.5px] border-current" />
              </ToggleGroupItem>
            </Tooltip>
            <Tooltip content={t`Circle`}>
              <ToggleGroupItem value="circle" className={toggleItemClass}>
                <div className="size-3.5 rounded-full border-[1.5px] border-current" />
              </ToggleGroupItem>
            </Tooltip>
          </ToggleGroup>
          <span className="w-7 text-right text-[10px] font-mono text-[var(--fg-4,#a3a3a3)]">{picture.borderRadius}</span>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-[var(--border,#e5e5e5)]" />

      {/* Effects */}
      <div className="flex flex-col gap-2">
        <div className={rowClass}>
          <Label
            htmlFor="picture.effects.hidden"
            className={cn(labelClass, "cursor-pointer", isStraightTemplate && "opacity-40")}
          >
            {t`Hide photo`}
          </Label>
          <Checkbox
            id="picture.effects.hidden"
            checked={isStraightTemplate ? true : picture.effects.hidden}
            disabled={isStraightTemplate}
            className={checkboxClass}
            onCheckedChange={(checked) => {
              if (!isStraightTemplate) setValue("basics.picture.effects.hidden", checked);
            }}
          />
        </div>

        <div className={rowClass}>
          <Label htmlFor="picture.effects.border" className={cn(labelClass, "cursor-pointer")}>
            {t`Border`}
          </Label>
          <Checkbox
            id="picture.effects.border"
            checked={picture.effects.border}
            className={checkboxClass}
            onCheckedChange={(checked) => setValue("basics.picture.effects.border", checked)}
          />
        </div>

        <div className={rowClass}>
          <Label htmlFor="picture.effects.grayscale" className={cn(labelClass, "cursor-pointer")}>
            {t`Grayscale`}
          </Label>
          <Checkbox
            id="picture.effects.grayscale"
            checked={picture.effects.grayscale}
            className={checkboxClass}
            onCheckedChange={(checked) => setValue("basics.picture.effects.grayscale", checked)}
          />
        </div>

        {isStraightTemplate && (
          <p className="text-[10px] text-[var(--fg-4,#a3a3a3)]">
            {t`Photo not supported by current template`}
          </p>
        )}
      </div>
    </div>
  );
};
